/**
 * The renderer's view of a snapshot: its sections, each fetched once through
 * the query cache, and the client names of its `namen` section. Every
 * section of one page view comes from the same snapshot, by its id, so a
 * recompute that finishes halfway through a visit never mixes two.
 */

import type { QueryClient } from "@tanstack/react-query";

import { type ApiError, bezettingSectionKey, fetchBezettingSection } from "@/lib/api";
import { apiErrorCode } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

import type { BezettingSectionLoader, BezettingSnapshotRef } from "./contract";
import { cachedSectionLoader } from "./snapshot-sections";

export { namesOf, snapshotRef } from "./snapshot-sections";

/** A failure worth asking again: no HTTP answer, or a server error; never a 4xx. */
function retryable(failureCount: number, error: unknown): boolean {
  const status = (error as Partial<ApiError> | null)?.status ?? 0;
  return failureCount < 2 && (status === 0 || status >= 500);
}

interface SectionLoaderOptions {
  /**
   * The snapshot is gone (pruned, or replaced): the host refetches the
   * summary and moves the page onto the newest snapshot.
   */
  onSnapshotGone: () => void;
  /**
   * The server refuses the app to this person now (403): the host asks the
   * summary and the app list again, and shows the denied page.
   */
  onDenied?: () => void;
  /** For tests: the request for one section. */
  fetchSection?: (snapshotId: string, key: string) => Promise<Record<string, unknown>>;
}

/**
 * The sections of one snapshot. `load` fetches a section at most once per
 * loader, cached by snapshot id and key with no expiry (a snapshot never
 * changes); a failed load is forgotten, so asking again tries again.
 * `tryLoad` answers null, without a request, for a key the snapshot does not
 * list, and for one the server says it does not hold.
 */
export function createSectionLoader(
  queryClient: QueryClient,
  snapshot: BezettingSnapshotRef,
  { onSnapshotGone, onDenied, fetchSection = fetchBezettingSection }: SectionLoaderOptions,
): BezettingSectionLoader {
  return cachedSectionLoader(snapshot, {
    fetch: (key) =>
      queryClient.fetchQuery({
        queryKey: bezettingSectionKey(snapshot.id, key),
        queryFn: () => fetchSection(snapshot.id, key),
        staleTime: Infinity,
        retry: retryable,
      }),
    codeOf: apiErrorCode,
    onFailure: (error) => {
      if (apiErrorCode(error) === "snapshot_not_found") {
        logger.debug("[bezetting]", "snapshot gone", snapshot.id);
        onSnapshotGone();
      } else if ((error as Partial<ApiError> | null)?.status === 403) {
        logger.debug("[bezetting]", "access lost");
        onDenied?.();
      }
    },
  });
}
